import React, { useCallback, useMemo, useRef, useState } from 'react';

import { FilterMatchMode } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableRowClickEvent,
  DataTableRowReorderEvent,
  DataTableSelectionSingleChangeEvent,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import { CalendarService } from 'components/services';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import TablePageHeader from 'ui/shared/TablePageHeader';

import { CalendarTemplate } from '../../constants';
import { isSelected } from '../../helpers';
import { useDebounce } from '../../hooks';
import { useApiCall } from '../../hooks/useApiCall';
import { ruLocale } from '../../lang/ruLocale';
import { BaseFilter, Calendar, PageRequest } from '../../types/interfaces';
import CalendarCard from '../../ui/CalendarCard';
import { clearFilter } from '../../utils';
import VisibilityWrapper from '../../utils/wrappers/VisibilityWrapper';
// Example of Table below

export default function Calendars() {
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [calendarToDelete, setCalendarToDelete] = useState<
    string | number | null
  >(null);
  const [selectedCalendar, setSelectedCalendar] =
    useState<Calendar>(CalendarTemplate);
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });
  const [totalCount, setTotalCount] = useState(1);
  const [first, setFirst] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);
  const onPageChanged = useCallback(async (event: DataTablePageEvent) => {
    if (event.rows !== undefined) {
      setFirst(0);
      setPageSize(event.rows);
    }
    if (event.page !== undefined) {
      setFirst(event.first);
      setPageNumber(event.first / event.rows + 1);
    }
  }, []);
  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
  }, []);
  const onSelectionChangeCallback = useCallback(
    (e: DataTableSelectionSingleChangeEvent<Calendar[]>) =>
      setSelectedCalendar(e.value),
    []
  );
  const onRowDoubleClickCallback = useCallback((e: DataTableRowClickEvent) => {
    const calendar = e.data as Calendar;
    setSelectedCalendar(calendar);
    setDialogVisible(true);
  }, []);
  const onRowReorderCallback = useCallback(
    (e: DataTableRowReorderEvent<Calendar[]>) => setCalendars(e.value),
    []
  );

  const filtersForApi = useMemo((): PageRequest => {
    const sort = multiSortMeta?.find((x) => x !== undefined);
    const newFilters = { ...filters };
    const baseFilters = Object.keys(newFilters).map((x): BaseFilter => {
      const f = newFilters[x];
      return {
        field: x,
        value: (f as DataTableFilterMetaData).value,
        operator: (f as DataTableFilterMetaData).matchMode
      };
    });
    return {
      pageNumber,
      pageSize,
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: JSON.stringify(baseFilters)
    };
  }, [filters, multiSortMeta, pageNumber, pageSize]);

  const [loading, setLoading] = useState<boolean>(true);
  const globalFilterRef = useRef<HTMLInputElement>(null);
  const calendarNameFilterRef = useRef<HTMLInputElement>(null);
  const calendarTypeFilterRef = useRef<HTMLInputElement>(null);
  const CalendarCardRef = useRef<null | { handleCancel: () => void }>(null);
  const toast = useRef<Toast>(null);
  const isSelectedIdExist = isSelected(selectedCalendar);

  addLocale('ru', ruLocale.ru);
  locale('ru');

  const getCalendars = useCallback(async () => {
    const result = await CalendarService.getAll(filtersForApi);
    if (result.data) {
      setTotalCount((result.data.total === 0 ? 1 : result.data.total) ?? 1);
    }
    setCalendars(result.data?.itemsList ?? []);
    setLoading(false);
  }, [filtersForApi]);
  useApiCall(getCalendars);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const value = typeof e === 'string' ? e : e.target.value;

    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setPageNumber(1);
    setFirst(0);
    setFilters(_filters);
  };

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    setPageNumber(1);
    setFirst(0);

    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleFilterChange = React.useCallback(
    (filter: string, e: React.ChangeEvent<HTMLInputElement> | string) => {
      debouncedOnFilterChange(filter, e);
    },
    [debouncedOnFilterChange]
  );

  const handleInputChange = useCallback(
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    },
    [debouncedOnFilterChange]
  );

  const handleDeleteConfirmation = () => {
    handleDelete(calendarToDelete);
    setDeleteDialogVisible(false);
  };

  const handleUpdate = async (
    id: string | number,
    updatedCalendar: Calendar
  ) => {
    const result = await CalendarService.update(Number(id), updatedCalendar);
    await getCalendars();
    return result.data;
  };

  const handleDelete = async (id: string | number | null) => {
    if (id != null)
      await CalendarService.delete(typeof id === 'string' ? parseInt(id) : id);
    await getCalendars();
    setSelectedCalendar(CalendarTemplate);
  };

  const handleAdd = async (calendar: Calendar) => {
    const result = await CalendarService.create(calendar);
    await getCalendars();
    return result.data;
  };

  const closeDialog = () => {
    setDialogVisible(false);
  };

  const onHideCalendarsCard = () => {
    if (CalendarCardRef.current !== null) {
      CalendarCardRef.current.handleCancel();
    }
  };

  const onOpenCard = useCallback((task: Calendar) => {
    setDialogVisible(true);
  }, []);

  const onAdd = useCallback(() => {
    setSelectedCalendar(CalendarTemplate);
    onOpenCard(CalendarTemplate);
  }, [setSelectedCalendar, onOpenCard]);

  const onEdit = useCallback(() => {
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите календарь для редактирования',
        life: 3000
      });
    }
    if (isSelectedIdExist) onOpenCard(selectedCalendar);
  }, [isSelectedIdExist, onOpenCard, selectedCalendar, toast]);

  const onDelete = useCallback(() => {
    if (isSelectedIdExist) {
      setCalendarToDelete(selectedCalendar.id);
      setDeleteDialogVisible(true);
    }
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите календарь для удаления',
        life: 3000
      });
    }
  }, [
    isSelectedIdExist,
    selectedCalendar?.id,
    setDeleteDialogVisible,
    setCalendarToDelete,
    toast
  ]);

  const header = (
    <TablePageHeader
      tableName="calendars"
      onGlobalSearchChange={(value) =>
        handleInputChange('global')({
          target: { value }
        } as React.ChangeEvent<HTMLInputElement>)
      }
      onAdd={onAdd}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  );

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const tableOffset = useMemo(() => {
    return tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
  }, [calendars]);

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement, tableOffset]);

  return (
    <div className="calendars-table wrapper wrapper--main">
      <Toast ref={toast} />

      <Dialog
        header="Карточка календаря"
        visible={dialogVisible}
        style={{ minWidth: '743px', width: '743px', minHeight: 'fit-content' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
        maximizable
        modal
        contentStyle={{ minHeight: 'fit-content' }}
        onHide={() => onHideCalendarsCard()}
      >
        <VisibilityWrapper code="calendar-card__roles">
          <CalendarCard
            calendarProps={selectedCalendar}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onAdd={handleAdd}
            closeDialog={closeDialog}
            ref={CalendarCardRef}
          />
        </VisibilityWrapper>
      </Dialog>

      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={handleDeleteConfirmation}
        entityType="календарь"
        entityName={selectedCalendar?.name}
      />
      <DataTable
        filters={filters}
        paginator
        first={first}
        rows={pageSize}
        onPage={onPageChanged}
        totalRecords={totalCount}
        rowsPerPageOptions={[10, 25, 50, 100]}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput"
        currentPageReportTemplate="{currentPage} из {totalPages}"
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedCalendar!}
        onSelectionChange={onSelectionChangeCallback}
        onRowDoubleClick={onRowDoubleClickCallback}
        metaKeySelection={false}
        scrollable
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={calendars}
        dataKey="id"
        filterDisplay="row"
        loading={loading}
        header={header}
        emptyMessage="Календари не найдены."
        onRowReorder={onRowReorderCallback}
        tableStyle={{ minWidth: '50rem' }}
        onFilter={onFiltersChange}
        paginatorLeft={' '}
        paginatorRight={`Всего: ${calendars.length > 0 ? totalCount : 0}`}
      >
        <Column
          columnKey="name"
          sortable
          field="name"
          header="Название календаря"
          filter
          onFilterClear={() =>
            clearFilter(
              'name',
              filters,
              setFilters,
              calendarNameFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={calendarNameFilterRef}
                onChange={handleInputChange('name')}
                placeholder="Поиск по названию календаря"
              />
            </div>
          }
          style={{ width: '30%', wordBreak: 'break-all' }}
        />
        <Column
          columnKey="description"
          sortable
          field="description"
          header="Описание"
          filter
          onFilterClear={() =>
            clearFilter(
              'description',
              filters,
              setFilters,
              calendarTypeFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={calendarTypeFilterRef}
                onChange={handleInputChange('description')}
                placeholder="Поиск по описанию"
              />
            </div>
          }
          style={{ width: '70%', wordBreak: 'break-all' }}
        />
      </DataTable>
    </div>
  );
}
